import { useQuery } from '@tanstack/react-query';
import { MSym } from '@storeo/ui';
import { api } from '../lib/api';
import { DemoBanner, PlaceholderTag } from '../components/lumina/Placeholder';

function pct(v: number): string {
  return `${(v * 100).toFixed(2)}%`;
}

function days(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (1000 * 60 * 60 * 24));
}

/** Admin · Auditoria & Logs (era "Saúde") — sinal acionável, não vaidade de número: lojas
 *  paradas em rascunho, chargeback fora da curva, campanha falhando. Consome
 *  `GET /api/admin/health`. Redesign Lumina: o protótipo "Auditoria, Logs & Compliance" é
 *  sobre WAF/HSM/ledger SHA-256/DEFCON — a Storeo não tem nada disso, roda sem firewall
 *  dedicado; a seção de baixo é ilustrativa, marcada "exemplo". Os 3 sinais reais acima
 *  ganharam o visual de "feed" do protótipo, mas o conteúdo é real. */
export function Saude() {
  const health = useQuery({ queryKey: ['admin', 'health'], queryFn: () => api.health() });
  const h = health.data;

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-surface-container-low p-space-xl shadow-xl">
        <div className="relative z-10 flex flex-col gap-space-2xs">
          <h1 className="font-headline-lg text-headline-lg text-on-surface font-bold">Auditoria & Logs</h1>
          <p className="font-body-md text-body-md text-on-surface-variant">O que precisa de atenção agora — não vaidade de número.</p>
        </div>
      </div>

      {health.isLoading ? (
        <p className="font-body-sm text-body-sm text-on-surface-variant">Carregando…</p>
      ) : (
        <>
          <section className="bg-surface-container-lowest rounded-2xl p-space-lg shadow-lg flex flex-col gap-space-sm font-mono">
            <div className="flex items-center gap-space-xs pb-space-xs border-b border-surface-container-highest/40">
              <MSym name="terminal" className="text-[18px] text-secondary" />
              <span className="font-label-sm text-label-sm text-on-surface-variant uppercase tracking-wider">Sinais operacionais</span>
            </div>

            {!h?.stuckInDraft.length ? (
              <LogLine tone="ok" text="Nenhuma loja parada em rascunho há mais de 7 dias." />
            ) : (
              h.stuckInDraft.map((s) => (
                <LogLine
                  key={s.id}
                  tone="warn"
                  text={
                    <>
                      Loja <b className="text-on-surface">{s.name}</b> ({s.subdomain}) parada em rascunho há{' '}
                      <b className="text-tertiary">{days(s.createdAt ?? new Date().toISOString())} dia(s)</b>.
                    </>
                  }
                />
              ))
            )}

            {!h?.chargebackOutliers.length ? (
              <LogLine tone="ok" text="Nenhum outlier de chargeback." />
            ) : (
              h.chargebackOutliers.map((c) => (
                <LogLine
                  key={c.id}
                  tone="error"
                  text={
                    <>
                      Chargeback fora da curva em <b className="text-on-surface">{c.name}</b> ({c.subdomain}): <b className="text-error">{pct(c.rate ?? 0)}</b>.
                    </>
                  }
                />
              ))
            )}

            {!h?.failingCampaigns.length ? (
              <LogLine tone="ok" text="Nenhuma campanha com falha alta." />
            ) : (
              h.failingCampaigns.map((c) => (
                <LogLine
                  key={c.id}
                  tone="error"
                  text={
                    <>
                      Campanha de <b className="text-on-surface">{c.storeName}</b> falhando: {c.failedCount}/{c.recipientCount} — “{c.text}”.
                    </>
                  }
                />
              ))
            )}
          </section>

          <section className="flex flex-col gap-space-md">
            <DemoBanner>
              O protótipo descreve auditoria imutável (ledger SHA-256), WAF global e antifraude por ML — a Storeo não roda nada disso hoje. Cards
              abaixo são ilustrativos.
            </DemoBanner>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-space-md">
              <SignalTile icon="shield" tone="secondary" label="Nível de Ameaça" value="DEFCON 5" sub="Heurística: sem desvio" />
              <SignalTile icon="policy" tone="primary" label="Bloqueios WAF (24h)" value="0" sub="Sem WAF dedicado" />
              <SignalTile icon="lock" tone="tertiary" label="Quarentena Antifraude" value="0 contas" sub="Sem motor de ML" />
              <SignalTile icon="verified" tone="secondary" label="Conformidade LGPD" value="—" sub="Sem auditoria formal" />
            </div>
          </section>
        </>
      )}
    </>
  );
}

const TONE_DOT: Record<string, string> = { ok: 'bg-secondary', warn: 'bg-tertiary', error: 'bg-error' };

function LogLine({ tone, text }: { tone: 'ok' | 'warn' | 'error'; text: React.ReactNode }) {
  return (
    <div className="flex items-start gap-space-xs px-space-sm py-space-2xs rounded-lg hover:bg-surface-container-low/60 transition-colors">
      <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${TONE_DOT[tone]}`} />
      <span className="text-body-sm font-body-sm text-on-surface-variant">{text}</span>
    </div>
  );
}

const TILE_CLS: Record<string, string> = {
  secondary: 'bg-secondary/10 text-secondary',
  primary: 'bg-primary/10 text-primary',
  tertiary: 'bg-tertiary/10 text-tertiary',
};

function SignalTile({ icon, tone, label, value, sub }: { icon: string; tone: string; label: string; value: string; sub: string }) {
  return (
    <div className="p-space-md rounded-xl bg-surface-container-low shadow-md flex flex-col gap-space-xs">
      <div className="flex items-center justify-between">
        <span className="font-label-sm text-label-sm uppercase tracking-wider text-outline flex items-center gap-1">
          {label} <PlaceholderTag />
        </span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${TILE_CLS[tone]}`}>
          <MSym name={icon} className="text-[18px]" />
        </div>
      </div>
      <span className="font-headline-sm text-headline-sm text-on-surface font-bold">{value}</span>
      <span className="text-[11px] text-on-surface-variant">{sub}</span>
    </div>
  );
}
